import React from "react";
import { Check, Minus } from "lucide-react";

/**
 * EligibilityCheckbox - Glassmorphism checkbox for Eligibility Assessment flow only
 * 
 * Design System:
 * - Visual box: 16-18px with glassmorphism styling
 * - Touch target: ≥40x40px via padding
 * - Checked state: White checkmark only (no blue fill)
 * - Glass body: Frosted outline matching Eligibility glass tokens
 * - Focus: White ring matching existing Eligibility focus-ring
 * 
 * Accessibility:
 * - Real <input type="checkbox"> for keyboard/screen readers
 * - Proper label association
 * - WCAG AA contrast maintained
 * - Touch targets ≥40px
 * 
 * @param {string} id - Unique identifier for the checkbox
 * @param {boolean} checked - Whether checkbox is checked
 * @param {function} onCheckedChange - Callback when state changes
 * @param {boolean} disabled - Whether checkbox is disabled
 * @param {boolean} indeterminate - Whether checkbox is in indeterminate state
 * @param {string} className - Additional classes for the wrapper
 * @param {boolean} compact - Smaller box and touch target (supporting-docs style)
 */
export default function EligibilityCheckbox({
  id,
  checked = false,
  onCheckedChange,
  disabled = false,
  indeterminate = false,
  className = "",
  compact = false,
}) {
  const checkboxRef = React.useRef(null);

  React.useEffect(() => {
    if (checkboxRef.current) {
      checkboxRef.current.indeterminate = indeterminate;
    }
  }, [indeterminate]);

  return (
    <div className={`relative inline-flex items-center justify-center ${className}`}>
      {/* Hidden but functional checkbox for accessibility */}
      <input
        ref={checkboxRef}
        id={id}
        type="checkbox"
        checked={checked}
        onChange={(e) => !disabled && onCheckedChange?.(e.target.checked)}
        disabled={disabled}
        className="sr-only peer"
        aria-checked={indeterminate ? "mixed" : checked}
      />
      
      {/* Visual checkbox - glassmorphism style */}
      <label
        htmlFor={id}
        data-testid="eligibility-checkbox-visual"
        className={`
          relative inline-flex items-center justify-center
          ${compact ? "h-8 w-8 min-h-8 min-w-8 p-1" : "h-[40px] w-[40px] p-2.5"}
          cursor-pointer
          ${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}
        `}
      >
        <div
          className={`
            ${compact ? "h-3.5 w-3.5" : "h-[17px] w-[17px]"} rounded
            border-2 transition-all duration-200
            flex items-center justify-center
            
          ${
            disabled
              ? "border-white/20 bg-white/5"
              : checked || indeterminate
              ? "border-blue-400 bg-blue-400"
              : "border-blue-400 bg-transparent"
          }
            
            peer-focus-visible:ring-2 peer-focus-visible:ring-blue-400/40 peer-focus-visible:ring-offset-2 peer-focus-visible:ring-offset-transparent
          `}
        >
          {/* Checkmark - white only when checked */}
          {checked && !indeterminate && (
            <Check 
              className={`${compact ? "h-2.5 w-2.5" : "h-[11px] w-[11px]"} text-white stroke-[3]`}
              strokeLinecap="round" 
              strokeLinejoin="round"
            />
          )}
          
          {/* Indeterminate dash */}
          {indeterminate && (
            <Minus 
              className={`${compact ? "h-2.5 w-2.5" : "h-[11px] w-[11px]"} text-white stroke-[3]`}
              strokeLinecap="round"
            />
          )}
        </div>
      </label>
    </div>
  );
}
